import { create } from 'zustand';
import type { User, UserRole, Role } from '../types';
import { userService } from '../services/user.service';

interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  phone?: string;
  role: UserRole;
}

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: 'active' | 'inactive';
}

interface UserState {
  users: User[];
  roles: Role[];
  selectedUser: User | null;
  isLoading: boolean;
  isLoadingRoles: boolean;
  error: string | null;

  // Actions
  fetchUsers: () => Promise<void>;
  fetchRoles: () => Promise<void>;
  createUser: (data: CreateUserData) => Promise<User>;
  updateUser: (id: string, data: UpdateUserData) => Promise<void>;
  updateUserRole: (userId: string, roleId: string) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  setSelectedUser: (user: User | null) => void;
  clearError: () => void;
}

export const useUserStore = create<UserState>((set) => ({
  users: [],
  roles: [],
  selectedUser: null,
  isLoading: false,
  isLoadingRoles: false,
  error: null,

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const users = await userService.getUsers();
      set({ users, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch users';
      set({ error: message, isLoading: false });
    }
  },

  fetchRoles: async () => {
    set({ isLoadingRoles: true });
    try {
      const roles = await userService.getRoles();
      set({ roles, isLoadingRoles: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch roles';
      set({ error: message, isLoadingRoles: false });
    }
  },

  createUser: async (data: CreateUserData) => {
    set({ isLoading: true, error: null });
    try {
      const newUser = await userService.createUser(data);
      set((state) => ({
        users: [...state.users, newUser],
        isLoading: false,
      }));
      return newUser;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create user';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateUser: async (id: string, data: UpdateUserData) => {
    set({ isLoading: true, error: null });
    try {
      const updatedUser = await userService.updateUser(id, data);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updatedUser : u)),
        selectedUser: state.selectedUser?.id === id ? updatedUser : state.selectedUser,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update user';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateUserRole: async (userId: string, roleId: string) => {
    set({ isLoading: true, error: null });
    try {
      const updatedUser = await userService.updateUserRole(userId, roleId);
      set((state) => ({
        users: state.users.map((u) => (u.id === userId ? updatedUser : u)),
        selectedUser: state.selectedUser?.id === userId ? updatedUser : state.selectedUser,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update user role';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteUser: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await userService.deleteUser(id);
      set((state) => ({
        users: state.users.filter((u) => u.id !== id),
        selectedUser: state.selectedUser?.id === id ? null : state.selectedUser,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete user';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setSelectedUser: (user: User | null) => set({ selectedUser: user }),

  clearError: () => set({ error: null }),
}));

